import { BuildingType } from '../constants';
import { TestRunner, TestWorld, TestDefinition, assert } from '../TestRunner';

// Test: Copper node + miner + belt scenario
const copperMiningTest: TestDefinition = {
  name: 'Copper Mining with Belt Transport',
  description:
    'Test that a miner on a copper node produces copper ore and moves it onto an adjacent belt',
  testFunction: async (world: TestWorld) => {
    // Create a 100x100 empty world (already done by TestWorld constructor)

    // Place a copper node at position (50, 50)
    world.placeResourceNode(50, 50, 'copper');

    // Verify the copper node was placed
    const copperTile = world.getTile(50, 50);
    assert.equals(copperTile, 'C', 'Copper node should be placed at (50, 50)');

    // Place a miner on the copper node
    const minerPlaced = world.placeBuilding(
      50,
      50,
      BuildingType.MINER,
      'right'
    );
    assert.isTrue(
      minerPlaced,
      'Miner should be successfully placed on copper node'
    );

    // Verify the miner is on the copper node
    const minerBuilding = world.getBuildingAt(50, 50);
    assert.isTrue(
      minerBuilding !== null,
      'Building should exist at miner position'
    );
    assert.equals(
      minerBuilding?.id,
      BuildingType.MINER,
      'Building should be a miner'
    );

    // Place a belt adjacent to the miner (to the right)
    const beltPlaced = world.placeBuilding(51, 50, BuildingType.BELT, 'right');
    assert.isTrue(
      beltPlaced,
      'Belt should be successfully placed adjacent to miner'
    );

    // Verify the belt is placed
    const beltBuilding = world.getBuildingAt(51, 50);
    assert.equals(
      beltBuilding?.id,
      BuildingType.BELT,
      'Building at (51, 50) should be a belt'
    );

    // Initially, there should be no items on the belt
    let itemsOnBelt = world.countItemsOnBelts('copper-ore');
    assert.equals(
      itemsOnBelt,
      0,
      'Initially, there should be no copper ore on belts'
    );

    // Simulate a few ticks to allow miner to produce and place items
    for (let i = 0; i < 10; i++) {
      world.simulateTick();
    }

    // After simulation, there should be copper ore on the belt
    itemsOnBelt = world.countItemsOnBelts('copper-ore');
    assert.greaterThan(
      itemsOnBelt,
      0,
      'After simulation, there should be copper ore on belts'
    );

    // Specifically check the belt adjacent to the miner
    const itemsOnAdjacentBelt = world.getItemsOnBelt(51, 50);
    assert.greaterThan(
      itemsOnAdjacentBelt.length,
      0,
      'The belt adjacent to the miner should have items'
    );

    // Verify the items are copper ore
    const copperOreItems = itemsOnAdjacentBelt.filter(
      item => item.item === 'copper-ore'
    );
    assert.greaterThan(
      copperOreItems.length,
      0,
      'The items should be copper ore'
    );

    return {
      name: 'Copper Mining with Belt Transport',
      passed: true,
      message: `Successfully produced ${itemsOnBelt} copper ore items on belts`,
      duration: 0, // Will be filled by test runner
      details: {
        copperOreItemsOnBelts: itemsOnBelt,
        itemsOnAdjacentBelt: itemsOnAdjacentBelt.length,
        copperOreItemsOnAdjacentBelt: copperOreItems.length,
      },
    };
  },
};

// Test: Iron mining test (similar but with iron)
const ironMiningTest: TestDefinition = {
  name: 'Iron Mining with Belt Transport',
  description:
    'Test that a miner on an iron node produces iron ore and moves it onto an adjacent belt',
  testFunction: async (world: TestWorld) => {
    // Place an iron node at position (40, 40)
    world.placeResourceNode(40, 40, 'iron');

    // Place a miner on the iron node
    const minerPlaced = world.placeBuilding(40, 40, BuildingType.MINER, 'down');
    assert.isTrue(
      minerPlaced,
      'Miner should be successfully placed on iron node'
    );

    // Place a belt adjacent to the miner (below it)
    const beltPlaced = world.placeBuilding(40, 41, BuildingType.BELT, 'down');
    assert.isTrue(
      beltPlaced,
      'Belt should be successfully placed adjacent to miner'
    );

    // Simulate production
    for (let i = 0; i < 15; i++) {
      world.simulateTick();
    }

    // Check for iron ore production
    const ironOreItems = world.countItemsOnBelts('iron-ore');
    assert.greaterThan(ironOreItems, 0, 'Should produce iron ore items');

    return {
      name: 'Iron Mining with Belt Transport',
      passed: true,
      message: `Successfully produced ${ironOreItems} iron ore items on belts`,
      duration: 0,
      details: {
        ironOreItemsOnBelts: ironOreItems,
      },
    };
  },
};

// Test: Belt movement
const beltMovementTest: TestDefinition = {
  name: 'Belt Item Movement',
  description: 'Test that items move along connected belts',
  testFunction: async (world: TestWorld) => {
    // Create a line of belts
    for (let x = 10; x <= 15; x++) {
      const placed = world.placeBuilding(x, 25, BuildingType.BELT, 'right');
      assert.isTrue(placed, `Belt should be placed at (${x}, 25)`);
    }

    // Add an item to the first belt
    const itemAdded = world.addItemToBelt(10, 25, 'copper-ore', 0);
    assert.isTrue(itemAdded, 'Item should be added to first belt');

    // Simulate enough ticks to move the item across all belts
    for (let i = 0; i < 100; i++) {
      world.simulateTick();
    }

    // The item should have moved through the belts
    // (In this simple simulation, it will disappear at the end)
    const totalItems = world.countItemsOnBelts();

    return {
      name: 'Belt Item Movement',
      passed: true,
      message: `Belt movement test completed with ${totalItems} items remaining`,
      duration: 0,
      details: {
        remainingItems: totalItems,
      },
    };
  },
};

// Export all tests
export const basicMiningTests = [
  copperMiningTest,
  ironMiningTest,
  beltMovementTest,
];

// Helper function to run just these tests
export async function runBasicMiningTests(): Promise<void> {
  const runner = new TestRunner();

  for (const test of basicMiningTests) {
    runner.addTest(test);
  }

  await runner.runAllTests();
}
